import { Button, Card, Grid, Header } from '@mairistem/composants';
import { getErrorMessage, Icon, PanelList } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import type Panel from '../../../classes/panels/Panel';
import getPanelList from '../../../constants/simulation/panels';
import TypePeriode from '../../../constants/simulation/typePeriode';
import type Simulation from '../../../interfaces/simulation/simulation';
import {
	addPinnedPanel,
	deletePinnedPanel,
	getPinnedPanel,
} from '../../../services/simulation/pinnedPanel';
import PanelComponent from './Panel';
import './Panels.css';

interface PanelsProps {
	simulation: Simulation;
}

const Panels = ({ simulation = null }: PanelsProps) => {
	const [panels, setPanels] = React.useState<Panel[]>([]);
	const [startIndexIndicateurs, setStartIndexIndicateurs] =
		React.useState<number>(0);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Un seul appel
	React.useEffect(() => {
		const fetchPanels = async () => {
			try {
				const { data } = await getPinnedPanel(
					simulation.exercice.identifiantEntite,
				);
				setPanels(
					getPanelList(simulation.budget.type).map((panel) => ({
						...panel,
						isVisible:
							panel.isMandatory || data.some((p) => p.code === panel.code),
					})),
				);
			} catch (e) {
				toast.error(
					getErrorMessage(e, 'Impossible de récuperer les indicateurs'),
				);
			}
		};

		fetchPanels();
	}, []);

	const handleRemovePanel = async (code: string) => {
		try {
			await deletePinnedPanel(code, simulation.exercice.identifiantEntite);
			setPanels((old) =>
				old.map((p) => ({
					...p,
					isVisible: p.code === code ? false : p.isVisible,
				})),
			);
		} catch (e) {
			toast.error(getErrorMessage(e, "Impossible de supprimer l'indicateur"));
		}
	};

	const handlePanelSelect = async (code: string) => {
		try {
			await addPinnedPanel(code, simulation.exercice.identifiantEntite);
			setPanels((old) =>
				old.map((p) => ({
					...p,
					isVisible: p.code === code ? true : p.isVisible,
				})),
			);
		} catch (e) {
			toast.error(getErrorMessage(e, "Impossible d'ajouter l'indicateur"));
		}
	};

	const handleStopPropagation = (e) => {
		e.stopPropagation();
	};

	const renderPanelList = () => {
		const remainingPanels = panels.filter((p) => !p.isVisible);
		return (
			// biome-ignore lint/a11y/noStaticElementInteractions: Span
			<span
				className="panelListContainer"
				data-testid="indicateursAddButton"
				onClick={handleStopPropagation}
				onKeyDown={handleStopPropagation}
				role="presentation"
			>
				<PanelList
					onSelect={handlePanelSelect}
					panels={remainingPanels}
					popupPosition="bottom right"
					popupTrigger={
						<Button
							icon="add"
							disabled={!remainingPanels.length}
							link={!!remainingPanels.length}
							content="Ajouter un indicateur"
						/>
					}
				/>
			</span>
		);
	};

	const handlePanelScrollStart = () => {
		setStartIndexIndicateurs((old) => old - 1);
	};

	const handlePanelScrollEnd = () => {
		setStartIndexIndicateurs((old) => old + 1);
	};

	const getPanelDataFromPeriode = () => {
		let data = null;
		switch (simulation.typePeriode) {
			case TypePeriode.DEMANDE:
				data = simulation.panelData.demande;
				break;
			case TypePeriode.PROPOSE:
				data = simulation.panelData.prop;
				break;
			case TypePeriode.VOTE:
				data = simulation.panelData.vote;
				break;
			case TypePeriode.CA_CFU:
				data = simulation.panelData.ca;
				break;
			default:
				break;
		}

		return {
			...data,
			...simulation.panelData,
			anneeExercice: simulation.exercice.anneeExercice,
			typeBudget: simulation.budget.type,
			transfertCpta: simulation.transfertCpta,
		};
	};

	const renderIndicateurs = () => {
		const indicateurs = [];
		const visiblePanels = panels.filter((panel) => panel.isVisible);
		let remaningPanelSize = 0;

		for (let i: number = startIndexIndicateurs; i < visiblePanels.length; i++) {
			remaningPanelSize += Number(visiblePanels[i].size);
			indicateurs.push(
				<PanelComponent
					code={visiblePanels[i].code}
					icon={visiblePanels[i].icon}
					isMandatory={visiblePanels[i].isMandatory}
					onRemove={() => handleRemovePanel(visiblePanels[i].code)}
					size={visiblePanels[i].size}
				>
					{visiblePanels[i].getComponent(getPanelDataFromPeriode())}
				</PanelComponent>,
			);
		}

		return (
			<>
				{startIndexIndicateurs > 0 && (
					<Icon
						className="start"
						data-testid="previousIndicateur"
						link
						name="arrow alternate circle left"
						onClick={handlePanelScrollStart}
					/>
				)}
				{indicateurs.map((indicateur) => indicateur)}
				{remaningPanelSize > 16 && (
					<Icon
						className="end"
						data-testid="nextIndicateur"
						link
						name="arrow alternate circle right"
						onClick={handlePanelScrollEnd}
					/>
				)}
			</>
		);
	};

	return (
		<Card className="indicateurs" fluid>
			<Card.Content>
				<Card.Meta as={Header}>
					INDICATEURS CLÉS
					{renderPanelList()}
				</Card.Meta>
				<Card.Description>
					<Grid className="fullWidth" stretched>
						<Grid.Row className="panelRow">{renderIndicateurs()}</Grid.Row>
					</Grid>
				</Card.Description>
			</Card.Content>
		</Card>
	);
};

export default Panels;
